<template>
  <div class="vh-100" id="app">
    <div class="app-inner">
      <loading-modal-component v-model:active="loading" :loading-title="loadingTitle"
        :loading-text="loadingText"></loading-modal-component>

      <error-message v-model:active="showError" :error-message="errorMessage" :error-title="errorTitle"></error-message>

      <header-component
        :session="session"
        :adminPortalData="adminPortalData"
        :policiesAccepted="policiesAccepted">
      </header-component>

      <router-view
        :session="session"
        :adminPortalData="adminPortalData"
        :adminPortalPrimaryProduct="adminPortalPrimaryProduct"
        :save-configuration="saveVehicleConfiguration"
        :saveConfigurationInline="saveVehicleConfigurationInline"
        :submit-order="submitVehicleOrder"
        @eulaAccepted="eulaAccepted = $event"
        @policiesAccepted="policiesAccepted = $event"
        @selectQuote="selectedQuote = $event"
        @canSubmitOrder="canSubmitOrder = $event"
        @stepOneAgreed="stepOneAgreed = $event"
        @stepTwoAgreed="stepTwoAgreed = $event"
        @stepThreeAgreed="stepThreeAgreed = $event"
        @tradeUpTradeDownAgreed="tradeUpTradeDownAgreed = $event"
      />

      <footer-component
        :eulaAccepted="eulaAccepted"
        :policiesAccepted="policiesAccepted"
        :yourProfileCompleted="yourProfileCompletedCheck"
        :nextPageTitle="nextPageTitle"
        :selectedQuote="selectedQuote"
        :canSubmitOrder="canSubmitOrder"
        :session="session"
        :adminPortalData="adminPortalData"
        :adminPortalPrimaryProduct="adminPortalPrimaryProduct"
        :stepOneAgreed="stepOneAgreed"
        :stepTwoAgreed="stepTwoAgreed"
        :stepThreeAgreed="stepThreeAgreed"
        :currentConfiguredQuote="currentConfiguredQuote"
        :tradeUpTradeDownAgreed="tradeUpTradeDownAgreed"
        @saveConfiguration="saveConfiguration($event)"
        @saveConfigurationInline="saveConfigurationInlineMethod($event)"
        @submitOrder="submitOrder"
      />

      <notifications group="notify"/>

    </div>

    <div id="screen-notice" class="screen-notice h-100 d-flex align-items-center justify-content-center">
      <div class="inner">
        <div class="mb-4"><img src="/images/notice-icon.png" alt="Screen Size Warning" class="notice-icon"></div>
        <div class="mb-5">
          <p class="lead">{{ deviceSizeMessageHeading }}</p>
          <p>{{ deviceSizeMessageDescription }}</p>
        </div>
        <div><img src="/images/notice-logo.png" alt="JCT | Origo" class="notice-logo"></div>
      </div>
    </div>
  </div>


</template>

<script>
import {EventBus} from '@/event-bus.js';
import HeaderComponent from "@/components/header/header-component.vue";
import LoadingModalComponent from './components/loading-modal-component';
import FooterComponent from "@/components/footer/footer-component.vue";
import ErrorMessage from "./components/error-message";
import {notify} from "@kyvg/vue3-notification";
import {useRoute} from 'vue-router'

export default {
  setup() {
    const route = useRoute()
    return { route }
  },
  components: {
    ErrorMessage,
    EventBus, LoadingModalComponent, FooterComponent, HeaderComponent
  },
  data() {
    return {
      selectedQuote: {
        quoteID: null,
        quoteName: null,
        type: null,
      },
      eulaAccepted: false,
      policiesAccepted: false,
      nextPageTitle: null, // change next page title from component if needed.
      session: {
        profile: null
      },
      error: null,
      loading: false,
      loadingExternal: false,
      loadingInternal: false,
      loadingTitle: null,
      loadingText: null,
      saveVehicleConfiguration: {},
      saveVehicleConfigurationInline: {},
      submitVehicleOrder: false,
      canSubmitOrder: false,
      stepOneAgreed: false,
      stepTwoAgreed: false,
      stepThreeAgreed: false,
      tradeUpTradeDownAgreed: false,
      deliveryNotes: '',
      confirmations: {
        employeeDetails: false,
        directDebitPayment: false,
        salaryDeductions: false,
        correctVehicle: false,
        onlyIncludedOnPersonalContractHireAgreement: false,
        maintenanceProduct: false,
        motorInsuranceProduct: false,
        earlyTerminationProduct: false,
        privacyNotice: false,
        keyPolicyPoints: false,
      },
      showError: false,
      errorTitle: null,
      errorMessage: null,
      currentConfiguredQuote: {
        color: null,
        trim: null,
        options: []
      },
      // orderFormValid: false,
      adminPortalData: {},
      adminPortalPrimaryProduct: {},
      yourProfileCompleted: false
    }
  },

  async beforeMount() {
    await this.fetchAdminPortalData();
  },

  methods: {
    // This controls "interal" loading ie session data fetching. Setting to false will only disable the modal if an
    //  external loading process isn't still ongoing
    setLoadingInternal(loading) {
      this.loadingInternal = loading;
      if (loading) {
        this.loading = true;
      } else if (!loading && !this.loadingExternal) {
        this.loading = false;
        this.loadingTitle = null;
        this.loadingText = null;
      }
    },

    setTheme(adminPortalDataContent) {
      const primaryColour = this.adminPortalData.content.customer?.primary_colour ? this.adminPortalData.content.customer.primary_colour : this.adminPortalData.content.global.primary_colour;
      const primaryTextColour = this.adminPortalData.content.customer?.primary_text_colour ? this.adminPortalData.content.customer.primary_text_colour : this.adminPortalData.content.global.primary_text_colour;
      const secondaryColour = this.adminPortalData.content.customer?.secondary_colour ? this.adminPortalData.content.customer.secondary_colour : this.adminPortalData.content.global.secondary_colour;
      const secondaryTextColour = this.adminPortalData.content.customer?.secondary_text_colour ? this.adminPortalData.content.customer.secondary_text_colour : this.adminPortalData.content.global.secondary_text_colour;
      const successColour = this.adminPortalData.content.customer?.success_colour ? this.adminPortalData.content.customer.success_colour : this.adminPortalData.content.global.success_colour;
      const successTextColour = this.adminPortalData.content.customer?.success_text_colour ? this.adminPortalData.content.customer.success_text_colour : this.adminPortalData.content.global.success_text_colour;
      const errorColour = this.adminPortalData.content.customer?.error_colour ? this.adminPortalData.content.customer.error_colour : this.adminPortalData.content.global.error_colour;
      const errorTextColour = this.adminPortalData.content.customer?.error_text_colour ? this.adminPortalData.content.customer.error_text_colour : this.adminPortalData.content.global.error_text_colour;

      document.head.insertAdjacentHTML('beforeend', '<style>'
        + ':root {'
        + '--primary-colour: ' + primaryColour + ';'
        + '--text-on-primary-colour: ' + primaryTextColour + ';'
        + '--secondary-colour: ' + secondaryColour + ';'
        + '--text-on-secondary-colour: ' + secondaryTextColour + ';'
        + '--success-colour: ' + successColour + ';'
        + '--text-on-success-colour: ' + successTextColour + ';'
        + '--error-colour: ' + errorColour + ';'
        + '--text-on-error-colour: ' + errorTextColour + ';'
        + '}'
        + '</style>')
    },
    async fetchAdminPortalData(forceRefresh = false) {
      console.debug('Fetching: ADMIN PORTAL DATA')
      await this.$http.get('/admin-portal-data' + (forceRefresh ? '?forceRefresh=1' : ''))
        .then(response => {

          if (response.data.status !== 200) {
            throw response.data.error.message;
          }

          console.debug('Retrieved: ADMIN PORTAL DATA')
          /*console.debug(response.data.data)*/
          this.adminPortalData = response.data.data;

          this.setTheme(this.adminPortalData.content);

          if (response.data.data.content.products) {
            if (response.data.data.content.products.salsac) {
              this.adminPortalPrimaryProduct = response.data.data.content.products.salsac[0];
              this.adminPortalPrimaryProduct.type = 'salsac';
            } else if (response.data.data.content.products.bch) {
              this.adminPortalPrimaryProduct = response.data.data.content.products.bch[0];
              this.adminPortalPrimaryProduct.type = 'bch';
            } else {
              this.adminPortalPrimaryProduct = response.data.data.content.products.ecoamaps[0];
              this.adminPortalPrimaryProduct.type = 'ecoamaps';
            }

          } else {
            if (!['Login', 'Request Password Reset', 'Request Password Reset Confirmation', 'Password Reset'].includes(this.$route.name)) {
              this.$router.push('/logout');
            }
          }
        })
        .catch(err => {
          this.setLoadingInternal(false);

          if (err) {
            notify({
              group: 'notify',
              type: 'error',
              text: err
            });

            this.error = err.toString();
          }

          if (!forceRefresh) {
            this.fetchAdminPortalData(true);
          }

          if (this.$route.name !== 'Login') {
            this.$router.push('/logout');
          }
        });
    },
    async fetchData(sessionKeys, customLoadingTitle) {

      this.setLoadingInternal(true);

      if (!customLoadingTitle) {
        if (sessionKeys != null && typeof sessionKeys[Symbol.iterator] === 'function') {
          let interval = 1000;
          sessionKeys.forEach((session_key, index) => {
            setTimeout(() => {
              this.loadingTitle = this.getFriendlySessionKeyTitle(session_key);
            }, index * interval);
          });
        } else {
          // Fallback
          this.loadingTitle = "Loading Data";
        }
      } else {
        this.loadingTitle = customLoadingTitle;
      }

      console.debug('Fetching: SESSION DATA')
      await this.$http.post('/session/update', {
        session_keys: sessionKeys
      })
        .then(response => {

          this.setLoadingInternal(false);

          console.debug('Retrieved: SESSION DATA')
          /*console.debug(response.data);*/

          this.session = response.data;

          this.policiesAccepted = collect(this.session.driverProfile.quotePolicies).where('accepted', false).count() === 0;

          this.yourProfileCompleted = this.session.driverProfile.profileCompleted;

          if (response.data.redirect) {
            this.$router.push({name: response.data.redirect});
          }

          if (this.session == null) {
            this.$router.push('/logout');
          }

        })
        .catch(err => {
          this.setLoadingInternal(false);

          let response = err.response;

          notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response?.data?.error
          });

          this.error = err.toString();

          if (this.$route.name !== 'Login') {
            this.$router.push('/logout');
          }
        });
    },
    getFriendlySessionKeyTitle(sessionKey) {
      if (sessionKey === 'drivergrades') {
        sessionKey = 'Determining Driver Grades';
      }
      if (sessionKey === 'driverprofile') {
        sessionKey = 'Fetching Driver Profile';
      }
      if (sessionKey === 'driverprofile/quotepolicies') {
        sessionKey = 'Fetching Policies';
      }
      if (sessionKey === 'order') {
        sessionKey = 'Checking Order System';
      }
      if (sessionKey === 'orders') {
        sessionKey = 'Loading Orders';
      }
      if (sessionKey === 'profile') {
        sessionKey = 'Verifying Profile';
      }
      if (sessionKey === 'shortlists') {
        sessionKey = 'Retrieving Shortlists';
      }
      if (sessionKey === 'vehiclesearch/filters/all') {
        sessionKey = 'Updating Filters';
      }
      if (sessionKey === 'vehiclesearch/derivatives') {
        sessionKey = 'Fetching Vehicles';
      }
      return sessionKey;
    },

    saveConfiguration(event) {
      this.saveVehicleConfiguration = event;
    },

    saveConfigurationInlineMethod(event) {
      // Set the saveVehicleConfigurationInline var to true, which is in turn watched in the car comparison
      //  components and used to trigger a save and redirect
      this.saveVehicleConfigurationInline = event;
    },

    async submitOrder() {
      this.setLoadingInternal(true);
      this.loadingTitle = 'Confirming Vehicle Availability';

      try {
        const response = await this.$http.post('/api/quote/' + this.$route.params.quoteId);
        if (new Date(response.data?.details?.quoteExpiryDate) < new Date()) {
          Vue.notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: "This quote has expired. Please refresh the quote, or select another vehicle."
          });

          this.$router.push({name: 'Car Comparison'});

        } else {
          this.loadingTitle = 'Submitting Order';

          this.$http.post('/api/submit-order', {
            quoteID: this.$route.params.quoteId,
            scheme: this.$route.meta.scheme,
            deliveryNotes: this.deliveryNotes,
            relatedReplacementQuotes: this.session?.driverProfile?.replacementQuotes?.[response.data.scheme]?.map(quote => quote.quoteID)

          }).then(response => {
            this.setLoadingInternal(false);

            if (response.data.status == 200) {

              notify({
                group: 'notify',
                type: 'success',
                title: 'Success',
                text: 'Order Request submitted successfully'
              });

              this.fetchData([
                'driverprofile',
                'profile',
                'orders',
              ]).then(() => {
                this.$router.push({name: 'Your Dashboard'});
              });
            }

            if (response.data.error) {
              throw response.data.error;
            }

          }).catch(err => {
            this.setLoadingInternal(false);
            let response = err.response.data;

            notify({
              group: 'notify',
              type: 'error',
              title: 'Error',
              text: response.error.message
            });
            this.error = err.message.toString();
            this.$router.push({name: response.redirect});
          });
        }
      } catch (err) {
        this.setLoadingInternal(false);

        notify({
          group: 'notify',
          type: 'error',
          title: 'Error',
          text: err
        });
      }
    },
  },

  mounted() {
    // This is "external" loading. This should be able to enable the loader, but only disable it if an internal loading
    //  process isn't also running at the same time
    EventBus.$on('loading', (loading) => {
      this.loadingExternal = loading;
      if (loading) {
        this.loading = true;
      } else if (!loading && !this.loadingInternal) {
        this.loading = false;
        this.loadingTitle = null;
        this.loadingText = null;
      }
    });

    EventBus.$on('loadingTitle', (loadingTitle) => {
      this.loadingTitle = loadingTitle;
    });

    EventBus.$on('loadingText', (loadingText) => {
      this.loadingText = loadingText;
    });

    EventBus.$on('clearSession', () => {
      this.session = [];
    });

    EventBus.$on('showError', (error) => {
      this.showError = error;
      if (!error) {
        this.errorTitle = null;
        this.errorMessage = null;
      }
    });

    EventBus.$on('errorTitle', (errorTitle) => {
      this.errorTitle = errorTitle;
    });

    EventBus.$on('errorMessage', (errorMessage) => {
      this.errorMessage = errorMessage;
    });

    EventBus.$on('updateConfiguredQuote', (quote) => {
      this.currentConfiguredQuote = quote;
    });

    EventBus.$on('yourProfileCompleted', (profileCompleted) => {
      this.yourProfileCompleted = profileCompleted;
    });

    EventBus.$on('refresh-session', (state) => {
      this.fetchData();
    });

    EventBus.$on('fetchAdminPortalData', (state) => {
      this.fetchAdminPortalData();
    });

    EventBus.$on('fetchData', (data) => {
      this.fetchData(data);
    });

    EventBus.$on('deliveryNoteUpdated', (note) => {
      this.deliveryNotes = note;
    });
  },

  computed: {
    yourProfileCompletedCheck() {
      return this.yourProfileCompleted;
    },

    deviceSizeMessageHeading() {
      return this.adminPortalData.content ? this.adminPortalData.content.global.device_size_message_heading : '';
    },

    deviceSizeMessageDescription() {
      return this.adminPortalData.content ? this.adminPortalData.content.global.device_size_message_description : '';
    },

    orderForm() {

      const order = (this.session.orders) ? this.session.orders.results[0] : this.session.order;
      if (!order) return false;

      const derivative = order.derivative;
      const driverProfile = this.session.driverProfile;
      const contact = this.session.driverProfile.contacts[0];
      const cashAllowance = this.session.driverProfile.currentCashAllowances.totalAnnualCurrentAllowance;
      const con = this.confirmations;

      const orderDetails = this.session.orderDetails;
      const orderDeliveryAddress = this.session.orderDeliveryAddress;

      /*** Step 1 ***/

      const employeeDetails = {
        name: 'Employee Details',
        fields: [
          {
            name: 'Name',
            value: contact.fullName
          },
          {
            name: 'Email Address',
            value: contact.emailAddress
          },
          {
            name: 'Grade',
            value: driverProfile.driverGrades[0].name
          },
          {
            name: 'Income tax band',
            value: (driverProfile.incomeTaxBand) ? driverProfile.incomeTaxBand.ratePercent + '%' : 'Unknown'
          },
          {
            name: 'Cash Allowance',
            value: '£' + cashAllowance
          },
          {
            name: 'Total Annual Mileage',
            value: Number(driverProfile.annualBusinessDistance) + Number(driverProfile.annualPrivateDistance)
          },
          {
            name: 'Business Annual Mileage',
            value: Number(driverProfile.annualBusinessDistance)
          },
          {
            name: 'Private Annual Mileage',
            value: Number(driverProfile.annualPrivateDistance)
          },
          {
            name: 'These are my employee details.',
            value: con.employeeDetails ? 'Yes' : 'No'
          }
        ]
      };
      const financialDetails = {
        name: 'Financial Details',
        fields: [
          {
            name: 'Contract Term',
            value: order.contractTerm + ' Months',
          },
          {
            name: 'Mileage',
            value: order.contractDistance,
          },
          {
            name: 'Monthly Vehicle Rental',
            value: "£" + Number(order.quote.details.pricing.totalExVAT).toFixed(2),
          },
          {
            name: 'ETi Insurance Cost',
            value: "£" + Number(order.quote.details.pricing.insuranceExVAT).toFixed(2),
          },
          {
            name: 'Total Monthly Direct Debit Cost',
            value: "£" + Number(order.quote.details.pricing.insuranceExVAT).toFixed(2),
          },
          {
            name: 'I accept that the above will by the monthly charge taken by direct debit for the provision of the vehicle.',
            value: con.directDebitPayment ? 'Yes' : 'No'
          }
        ]
      };
      const salaryDetails = {
        name: 'Salary Deductions',
        fields: [
          {
            name: 'Fleet Vehicle Insurance',
            value: "£" + driverProfile.vehicleInsuranceAnnualCost,
          },
          {
            name: 'Fuel Provision',
            value: '[UNKNOWN]',
          },
          {
            name: 'I accept that the above will be deducted from my salary for the provision of the vehicle.',
            value: con.salaryDeductions ? 'True' : 'False',
          }
        ]
      };
      const vehicleDetails = {
        name: 'Vehicle Details',
        fields: [
          {
            name: 'Manufacturer',
            value: derivative.manufacturerName,
          },
          {
            name: 'Range',
            value: derivative.rangeName,
          },
          {
            name: 'Model',
            value: derivative.modelName,
          },
          {
            name: 'Derivative',
            value: derivative.derivativeName,
          },
        ]
      };


      const deliveryAddressDetails = {
        name: 'Delivery Address',
        fields: [
          {
            name: 'Address',
            value: orderDeliveryAddress
          }
        ]
      }

      if (!orderDetails) {
        return {
          name: 'Company Car Order',
          orderId: order.orderID,
          steps: [
            {
              name: '1. Order Form',
              sections: [
                employeeDetails,
                financialDetails,
                salaryDetails,
                vehicleDetails,
                deliveryAddressDetails
              ]
            }
          ]
        }
      }

      /*** Step 2 ***/

      const personalInfo = {
        name: 'Personal Information',
        fields: [
          {
            name: 'Title',
            value: orderDetails.personalInformation[0].title
          },
          {
            name: 'Forename',
            value: orderDetails.personalInformation[0].forename
          },
          {
            name: 'Middle Name',
            value: orderDetails.personalInformation[0].middle_name
          },
          {
            name: 'Surname',
            value: orderDetails.personalInformation[0].surname
          },
          {
            name: 'Date of Birth',
            value: orderDetails.personalInformation[0].date_of_birth
          },
          {
            name: 'Primary Telephone',
            value: orderDetails.personalInformation[0].primary_telephone
          },
          {
            name: 'Secondary Telephone',
            value: orderDetails.personalInformation[0].secondary_telephone
          },
          {
            name: 'Email Address',
            value: orderDetails.personalInformation[0].email_address
          },
          {
            name: 'Gender',
            value: orderDetails.personalInformation[0].gender
          },
          {
            name: 'Nationality',
            value: orderDetails.personalInformation[0].nationality
          },
          {
            name: 'Marital Status',
            value: orderDetails.personalInformation[0].marital_status
          },
          {
            name: 'Number of Dependants',
            value: orderDetails.personalInformation[0].number_of_dependants
          },
          {
            name: 'Driving License Number',
            value: orderDetails.personalInformation[0].driving_licence_number
          },

        ]
      };

      let addressFields = [];
      orderDetails.addressDetails.forEach((address, index) => {
        const addressValues = [
          {
            name: 'Address 1',
            value: address.address_line_one
          },
          {
            name: 'Address 2',
            value: address.address_line_two
          },
          {
            name: 'Town',
            value: address.town
          },
          {
            name: 'County',
            value: address.county
          },
          {
            name: 'Postcode',
            value: address.postcode
          },
          {
            name: 'Years at address',
            value: address.years_at_address
          },
          {
            name: 'Months at address',
            value: address.months_at_address
          },
          {
            name: 'Accomodation Basis',
            value: address.accommodation_basis
          },
        ]

        if (index > 0) {
          addressValues.unshift({
            name: '(Address ' + (index + 1) + ')',
            value: ''
          });
        }

        addressFields.push(...addressValues);
      });

      const addresses = {
        name: 'Address/es for the Last 3 Years',
        fields: addressFields
      }

      let employmentFields = [];
      orderDetails.employmentHistory.forEach((employment, index) => {
        const employmentValues = [
          {
            name: 'Employer',
            value: employment.employer
          },
          {
            name: 'Occupation',
            value: employment.occupation
          },
          {
            name: 'Employment Status',
            value: employment.employment_status
          },
          {
            name: 'Work Phone Number',
            value: employment.work_phone_number
          },
          {
            name: 'Address Line 1',
            value: employment.address_line_one
          },
          {
            name: 'Address Line 2',
            value: employment.address_line_two
          },
          {
            name: 'Town',
            value: employment.town
          },
          {
            name: 'County',
            value: employment.county
          },
          {
            name: 'Postcode',
            value: employment.postcode
          },
          {
            name: 'Years at Employment',
            value: employment.years_at_employment
          },
          {
            name: 'Months at Employment',
            value: employment.months_at_employment
          },
          {
            name: 'Gross Annual Salary',
            value: employment.gross_annual_salary
          },

        ]

        if (index > 0) {
          employmentValues.unshift({
            name: '(Address ' + (index + 1) + ')',
            value: ''
          });
        }

        employmentFields.push(...employmentValues);
      });
      const employment = {
        name: 'Employment for the Last 3 Years',
        fields: employmentFields
      };

      const bankDetails = {
        name: 'Bank Details',
        fields: [
          {
            name: 'Account Name',
            value: orderDetails.bankDetails[0].account_name
          },
          {
            name: 'Account Type',
            value: orderDetails.bankDetails[0].account_type
          },
          {
            name: 'Sort Code',
            value: orderDetails.bankDetails[0].sort_code
          },
          {
            name: 'Account Number',
            value: orderDetails.bankDetails[0].account_number
          },
          {
            name: 'Years at bank',
            value: orderDetails.bankDetails[0].years_at_bank
          },
          {
            name: 'Bank Name',
            value: orderDetails.bankDetails[0].bank_name
          },
          {
            name: 'Branch Name',
            value: orderDetails.bankDetails[0].branch_name
          },
          {
            name: 'Address Line One',
            value: orderDetails.bankDetails[0].address_line_one
          },
          {
            name: 'Address Line Two',
            value: orderDetails.bankDetails[0].address_line_two
          },
          {
            name: 'Town',
            value: orderDetails.bankDetails[0].town
          },
          {
            name: 'Country',
            value: orderDetails.bankDetails[0].county
          },
          {
            name: 'Postcode',
            value: orderDetails.bankDetails[0].postcode
          },
        ]
      };

      const affordability = {
        name: 'Affordability',
        fields: [
          {
            name: 'Take home pay',
            value: orderDetails.affordability[0].take_home_pay_per_month
          },
          {
            name: 'Rent Mortgage',
            value: orderDetails.affordability[0].rent_mortgage
          },
          {
            name: 'Credit Card Loans',
            value: orderDetails.affordability[0].credit_card_loans
          },
          {
            name: 'Utilities Household',
            value: orderDetails.affordability[0].utilities_household
          },
          {
            name: 'Insurance Subscriptions',
            value: orderDetails.affordability[0].insurance_subscriptions
          },
          {
            name: 'Total Expenditure',
            value: orderDetails.affordability[0].total_expenditure
          },
        ]
      }

      return {
        name: 'Structured Cash Alternative Order',
        orderId: order.orderID,
        steps: [
          {
            name: '1. Order Form',
            sections: [
              employeeDetails,
              financialDetails,
              salaryDetails,
              vehicleDetails,
              deliveryAddressDetails
            ]
          },
          {
            name: '2. Credit Proposal Acceptance',
            sections: [
              personalInfo,
              addresses,
              employment,
              bankDetails,
              affordability
            ]
          }
        ]
      }
    }
  },
}
</script>
